'use client';

import { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';

import jsCookie from 'js-cookie';

import { REMEMBER_ME_SESSION_IDLE_DURATION_HOURS, REMEMBER_ME_SESSION_TRACKING_KEY } from '@/config/constants';
import useWebAlias from '@/helpers/hooks/useWebalias';
import { RootState } from '@/store';

import { isSessionCookieExpired } from '../utils/idleTrackingUtils';

const SESSION_VALIDITY_HOURS = REMEMBER_ME_SESSION_IDLE_DURATION_HOURS; // Use centralized constant from config

export const useIdleTracking = () => {
  const [shouldTrackIdle, setShouldTrackIdle] = useState(false);
  const [lastCoordinationFlag, setLastCoordinationFlag] = useState<number | null>(null);
  const { webalias, generateURL } = useWebAlias();

  // Get authentication status from Redux store
  const { isAuthenticated, isAuthenticationLoading } = useSelector(
    (state: RootState) => state.slices.authenticationSlice
  );

  // eslint-disable-next-line sonarjs/cognitive-complexity
  const checkSessionCookie = useCallback(() => {
    console.log('🔍 [IdleTracking] Checking session cookie...');
    console.log(
      '🔐 [IdleTracking] Auth status - isAuthenticated:',
      isAuthenticated,
      'isLoading:',
      isAuthenticationLoading
    );

    // Don't start tracking while authentication is still loading
    if (isAuthenticationLoading) {
      console.log('⏳ [IdleTracking] Authentication still loading - waiting...');
      setShouldTrackIdle(false);
      return;
    }

    // CRITICAL: Don't track idle state for users who aren't logged in
    // BUT be VERY careful about cleanup - avoid removing cookies that were just created during login
    if (!isAuthenticated) {
      console.log('❌ [IdleTracking] User not authenticated - not tracking idle state');

      // Check if there's a session cookie present
      const sessionCookie = jsCookie.get(REMEMBER_ME_SESSION_TRACKING_KEY);
      if (sessionCookie) {
        console.log('🔍 [IdleTracking] Found session cookie for unauthenticated user:', sessionCookie);

        // Be extra cautious about cleanup - check if this might be a recent login
        try {
          const cookieData = JSON.parse(sessionCookie);
          const cookieAge = Date.now() - parseInt(cookieData.timestamp, 10);
          const ageInSeconds = cookieAge / 1000;

          console.log(`🕐 [IdleTracking] Cookie age: ${ageInSeconds.toFixed(1)} seconds`);

          // Don't clean up cookies that are less than 10 seconds old
          // This prevents removing cookies that were just created during login
          if (ageInSeconds < 10) {
            console.log('⏳ [IdleTracking] Cookie is very recent, likely from ongoing login - skipping cleanup');
            setShouldTrackIdle(false);
            return;
          }
        } catch (error) {
          console.warn('⚠️ [IdleTracking] Could not parse cookie timestamp for age check:', error);
        }

        // Only clean up cookies if authentication loading is complete AND cookie is not brand new
        if (!isAuthenticationLoading) {
          console.log('🧹 [IdleTracking] Cleaning up stale session cookie for confirmed unauthenticated user');
          jsCookie.remove(REMEMBER_ME_SESSION_TRACKING_KEY);
          // Also try to remove from root domain
          try {
            const cookieData = JSON.parse(sessionCookie);
            if (cookieData.domain) {
              jsCookie.remove(REMEMBER_ME_SESSION_TRACKING_KEY, { domain: cookieData.domain });
            }
          } catch (error) {
            // If parsing fails, try common root domain patterns
            const rootDomain = `.${window.location.host.split('.').slice(-2).join('.')}`;
            jsCookie.remove(REMEMBER_ME_SESSION_TRACKING_KEY, { domain: rootDomain });
          }
        } else {
          console.log(
            '⏳ [IdleTracking] Authentication loading - skipping cookie cleanup to avoid removing valid cookies'
          );
        }
      } else {
        console.log('✅ [IdleTracking] No session cookie found for unauthenticated user - this is expected');
      }

      setShouldTrackIdle(false);
      return;
    }

    const sessionCookie = jsCookie.get(REMEMBER_ME_SESSION_TRACKING_KEY);

    if (sessionCookie) {
      console.log('🍪 [IdleTracking] Session cookie found:', sessionCookie);
      console.log('📋 [IdleTracking] This means user logged in WITHOUT "Remember Me" checked');

      // FIRST: Check immediately for idle timeout before any other processing
      try {
        const cookieData = JSON.parse(sessionCookie);

        // If we detect idle_timeout, immediately force logout regardless of other conditions
        if (cookieData.logoutReason === 'idle_timeout') {
          console.log('🚨 [IdleTracking] IDLE TIMEOUT DETECTED - FORCING IMMEDIATE LOGOUT');

          // Determine logout URL based on current location
          const isBackoffice =
            window.location.pathname.includes('backoffice') ||
            window.location.host.includes('backoffice') ||
            window.location.host.includes('petpro');

          const logoutUrl = isBackoffice ? '/logout' : generateURL('/account/logout', webalias);

          console.log('🔀 [IdleTracking] Redirecting to logout due to idle timeout:', logoutUrl);
          window.location.href = logoutUrl;
          return; // Exit immediately to prevent further processing
        }
      } catch (idleCheckError) {
        console.warn('⚠️ [IdleTracking] Failed to check for idle timeout:', idleCheckError);
      }

      // 🚨 Check for coordination flag changes for immediate logout detection
      try {
        const cookieData = JSON.parse(sessionCookie);
        if (cookieData.coordinationFlag && cookieData.coordinationFlag !== lastCoordinationFlag) {
          console.log('📡 [IdleTracking] Coordination flag change detected:', {
            previous: lastCoordinationFlag,
            current: cookieData.coordinationFlag,
            reason: cookieData.logoutReason,
          });
          setLastCoordinationFlag(cookieData.coordinationFlag);

          // If this is an idle timeout from another domain, immediately force logout
          if (cookieData.logoutReason === 'idle_timeout') {
            console.log('🚨 [IdleTracking] Cross-domain idle logout detected - FORCING IMMEDIATE LOGOUT');

            // Determine logout URL based on current location
            const isBackoffice =
              window.location.pathname.includes('backoffice') ||
              window.location.host.includes('backoffice') ||
              window.location.host.includes('petpro');

            const logoutUrl = isBackoffice ? '/logout' : generateURL('/account/logout', webalias);

            console.log('🔀 [IdleTracking] Redirecting to logout:', logoutUrl);
            window.location.href = logoutUrl;
            return; // Exit early to prevent further processing
          }
        }
      } catch (flagError) {
        console.warn('⚠️ [IdleTracking] Failed to check coordination flag:', flagError);
      }

      // Use the centralized validation function that handles domain exclusion
      // Disable auto-cleanup here to avoid double-logout issues
      const sessionStatus = isSessionCookieExpired(false); // Disable auto-cleanup
      console.log('📊 [IdleTracking] Session status:', sessionStatus);

      if (!sessionStatus.exists) {
        console.log('❌ [IdleTracking] Session does not exist - not tracking');
        setShouldTrackIdle(false);
        return;
      }

      if (sessionStatus.isExcluded) {
        // Current domain is excluded from this session - don't track at all
        console.log('🚫 [IdleTracking] Current domain is excluded from session tracking');
        setShouldTrackIdle(false);
        return;
      }

      if (sessionStatus.isExpired) {
        // Session has expired - only track for logout if user is still authenticated
        // This handles cases where user was idle on another domain and we need to logout here
        if (isAuthenticated) {
          console.log(
            '⏰ [IdleTracking] Session has expired but user still authenticated - tracking for logout handling'
          );
          setShouldTrackIdle(true);
        } else {
          console.log('⏰ [IdleTracking] Session has expired and user not authenticated - not tracking');
          setShouldTrackIdle(false);
        }
        return;
      }

      // Session is valid and current domain is not excluded
      console.log('✅ [IdleTracking] Session is valid - starting tracking');
      setShouldTrackIdle(true);
    } else {
      console.log('❌ [IdleTracking] No session cookie found - not tracking');
      console.log('🔍 [IdleTracking] Debugging cookie search:');
      console.log('   - Cookie key:', REMEMBER_ME_SESSION_TRACKING_KEY);
      console.log('   - Current domain:', window.location.hostname);
      console.log('   - Current host:', window.location.host);
      console.log('   - Expected cookie domain:', `.${window.location.host.split('.').slice(-2).join('.')}`);
      console.log('   - All cookies:', document.cookie);
      console.log(
        '📋 [IdleTracking] This means user likely logged in WITH "Remember Me" checked (no idle tracking needed)'
      );
      setShouldTrackIdle(false);
    }
  }, [isAuthenticated, isAuthenticationLoading]); // Include auth dependencies

  useEffect(() => {
    console.log('🚀 [IdleTracking] Hook initialized, checking session...');

    // Immediate check on mount to catch any existing idle timeout markers
    checkSessionCookie();

    // Check every 1 minute for session status
    const interval = setInterval(checkSessionCookie, 60000); // Check every 1 minute (60000ms)

    // Add window focus listener to immediately check when user switches back to tab
    const handleWindowFocus = () => {
      console.log('👁️ [IdleTracking] Window focused - immediate session check');
      checkSessionCookie();
    };

    window.addEventListener('focus', handleWindowFocus);
    window.addEventListener('visibilitychange', handleWindowFocus);

    return () => {
      clearInterval(interval);
      window.removeEventListener('focus', handleWindowFocus);
      window.removeEventListener('visibilitychange', handleWindowFocus);
    };
  }, [checkSessionCookie]); // No auth dependency to avoid circular issues

  useEffect(() => {
    console.log('🎯 [IdleTracking] shouldTrackIdle changed to:', shouldTrackIdle);
  }, [shouldTrackIdle]);

  return { shouldTrackIdle };
};
